// Hook de presencia para admins: muestra quien esta conectado al panel y en
// que pantalla esta navegando. Usa Supabase Realtime presence channel.
//
// Uso:
//   const admins = useAdminPresence()
//   admins => [{ user_id, nombre, email, path, last_seen }, ...]
//
// Se actualiza automaticamente cuando alguien entra/sale del panel
// o cambia de pagina (via useLocation).

import { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from './supabase'
import { useAuth } from './AuthContext'

const CHANNEL_NAME = 'admins-online'

export function useAdminPresence() {
  const { user, profile, role } = useAuth()
  const location = useLocation()
  const [admins, setAdmins] = useState([])
  const channelRef = useRef(null)

  // Suscripcion al canal (una sola vez por sesion de admin)
  useEffect(() => {
    if (!user?.id || (role !== 'admin' && role !== 'superadmin')) return

    const channel = supabase.channel(CHANNEL_NAME, {
      config: { presence: { key: user.id } },
    })

    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState()
      // state = { 'uuid1': [{...meta1}], 'uuid2': [{...meta2}], ... }
      // Si un mismo user tiene varias tabs, hay multiples metas. Tomamos la mas reciente.
      const flat = []
      for (const [key, metas] of Object.entries(state)) {
        if (!metas?.length) continue
        const latest = metas.reduce((a, b) =>
          new Date(b.last_seen || 0) > new Date(a.last_seen || 0) ? b : a
        )
        flat.push({ user_id: key, ...latest })
      }
      setAdmins(flat)
    })

    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({
          user_id: user.id,
          nombre: profile?.nombre || user.email?.split('@')[0] || 'Admin',
          email: user.email,
          path: location.pathname,
          last_seen: new Date().toISOString(),
        })
      }
    })

    channelRef.current = channel

    return () => {
      supabase.removeChannel(channel)
      channelRef.current = null
    }
    // Solo re-suscribir cuando cambia el user o el rol
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, role, profile?.nombre])

  // Re-track cuando cambia el path (sin reconectar canal)
  useEffect(() => {
    if (!channelRef.current || !user?.id || (role !== 'admin' && role !== 'superadmin')) return
    channelRef.current.track({
      user_id: user.id,
      nombre: profile?.nombre || user.email?.split('@')[0] || 'Admin',
      email: user.email,
      path: location.pathname,
      last_seen: new Date().toISOString(),
    })
  }, [location.pathname, user?.id, role, profile?.nombre, user?.email])

  return admins
}

/** Resuelve un path a un label legible usando el MENU del AdminLayout.
 *  Soporta grupos con `items` y grupos con `subgroups`.
 *  Ej: '/admin/clientes' -> 'Suscriptores' */
export function pathToLabel(path, menu) {
  if (!path) return ''
  // Aplanar todos los items linkeables del menú (links top-level + items de groups + items de subgroups)
  const flat = []
  for (const item of menu) {
    if (item.type === 'link') flat.push({ to: item.to, label: item.label })
    if (item.type === 'group') {
      for (const sub of item.items || []) flat.push({ to: sub.to, label: sub.label })
      for (const sg of item.subgroups || []) {
        for (const sub of sg.items || []) flat.push({ to: sub.to, label: sub.label })
      }
    }
  }
  // Match exacto
  for (const it of flat) if (it.to === path) return it.label
  // Match por prefijo (más largo gana)
  let best = null
  for (const it of flat) {
    if (it.to === '/admin') continue
    if (path.startsWith(it.to)) {
      if (!best || it.to.length > best.to.length) best = it
    }
  }
  if (best) return best.label
  return path.replace(/^\/admin\/?/, '') || 'Dashboard'
}
